// File System Access API helpers + handle persistence in IndexedDB.
//
// IMPORTANT: Only the FileSystemHandle (a *reference* to the file the user
// picked) is stored in IndexedDB — never any user data. All actual content
// (company, parties, invoices) lives in the user-controlled .db file on disk.
//
// Falls back gracefully in Firefox / Safari / mobile (no handle persistence,
// user must Save / Open via download / upload each session).

import { get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";

const HANDLE_KEY = "ezbiz.dbHandle";

export const supportsFileSystemAccess =
  typeof window !== "undefined" &&
  typeof window.showSaveFilePicker === "function" &&
  typeof window.showOpenFilePicker === "function";

const ACCEPT = {
  description: "Ezbiz Invoice Database",
  accept: { "application/x-sqlite3": [".db", ".sqlite"] },
};

export async function pickNewDbFile() {
  if (!supportsFileSystemAccess) return null;
  const handle = await window.showSaveFilePicker({
    suggestedName: "ezbiz.db",
    types: [ACCEPT],
  });
  await idbSet(HANDLE_KEY, handle);
  return handle;
}

export async function pickExistingDbFile() {
  if (!supportsFileSystemAccess) return null;
  const [handle] = await window.showOpenFilePicker({
    multiple: false,
    types: [ACCEPT],
  });
  await idbSet(HANDLE_KEY, handle);
  return handle;
}

export async function loadStoredHandle() {
  if (!supportsFileSystemAccess) return null;
  try {
    const handle = await idbGet(HANDLE_KEY);
    if (!handle) return null;
    // Verify we still have permission. In Chrome the user is prompted once
    // per session for write permission on a previously-granted handle.
    const opts = { mode: "readwrite" };
    let perm = await handle.queryPermission(opts);
    if (perm === "granted") return handle;
    perm = await handle.requestPermission(opts);
    return perm === "granted" ? handle : null;
  } catch {
    return null;
  }
}

export async function clearStoredHandle() {
  try {
    await idbDel(HANDLE_KEY);
  } catch {
    /* ignore */
  }
}

export async function readBlob(handle) {
  if (!handle) return null;
  const file = await handle.getFile();
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

export async function writeBlob(handle, uint8) {
  if (!handle) return false;
  const writable = await handle.createWritable();
  await writable.write(uint8);
  await writable.close();
  return true;
}

export async function getHandleName(handle) {
  if (!handle) return "";
  try {
    return handle.name || "";
  } catch {
    return "";
  }
}

// ----- Manual fallback (works in every browser) -----

export function downloadBlob(uint8, suggestedName = "ezbiz.db") {
  const blob = new Blob([uint8], { type: "application/x-sqlite3" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function uploadBlob() {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".db,.sqlite,application/x-sqlite3,application/octet-stream";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      const buf = await file.arrayBuffer();
      resolve({ name: file.name, bytes: new Uint8Array(buf) });
    };
    input.click();
  });
}
